import type { CompletedWorkItem, TopicState } from '../types'
import {
  ingestionInputFromCompletedWork,
  isAttemptIngestibleForEngine,
} from './derivations'
import type { TriggerActionType } from './processing-trigger-policy'
import {
  type PendingProcessingSummary,
  summarizePendingProcessing,
} from './processing-trigger-policy'

export type RecommendationEngineTabId =
  | 'progress'
  | 'proficiency-goals'
  | 'graph'
  | 'recommendations'
  | 'homework-review'
  | 'calculation-log'
  | 'setup'

export interface PlanSnapshot {
  weeklyCommitmentHours: number
  targetDate: string | null
}

export interface RecommendationItemSnapshot {
  id: string
  status: 'suggested' | 'assigned' | 'postponed' | 'rejected'
}

export interface CompletedWorkSnapshot {
  id: string
  needsGrading: boolean
  reportedApproachType: string | null
}

export interface SnapshotState {
  topics: TopicState[]
  plan: PlanSnapshot
  completedWork: CompletedWorkSnapshot[]
  recommendations: RecommendationItemSnapshot[]
  graphJson: {
    adjacencyList: Record<string, string[]>
    meta: Record<
      string,
      {
        niche: number
        learning_order: number
      }
    >
  } | null
}

export interface CurrentState {
  topics: TopicState[]
  plan: PlanSnapshot
  completedWork: CompletedWorkItem[]
  recommendations: RecommendationItemSnapshot[]
  graphJson: {
    adjacencyList: Record<string, string[]>
    meta: Record<
      string,
      {
        niche: number
        learning_order: number
      }
    >
  } | null
}

export interface DiffItem {
  key: string
  label: string
  tabId: RecommendationEngineTabId
  previousValue: string | number
  currentValue: string | number
  triggerActionType: TriggerActionType
}

export interface PendingDiffResult {
  items: DiffItem[]
  triggerActions: TriggerActionType[]
}

export function buildSnapshotState(input: CurrentState): SnapshotState {
  return {
    topics: structuredClone(input.topics),
    plan: structuredClone(input.plan),
    completedWork: input.completedWork.map((work) => ({
      id: work.id,
      needsGrading: work.needsGrading,
      reportedApproachType: work.reportedApproach?.type ?? null,
    })),
    recommendations: structuredClone(input.recommendations),
    graphJson: structuredClone(input.graphJson),
  }
}

function compareTopicSettings(
  snapshot: SnapshotState,
  current: CurrentState,
): DiffItem[] {
  const items: DiffItem[] = []
  const currentById = new Map(current.topics.map((topic) => [topic.id, topic]))
  for (const snapshotTopic of snapshot.topics) {
    const currentTopic = currentById.get(snapshotTopic.id)
    if (!currentTopic) continue

    if (
      snapshotTopic.minProficiencyThreshold !==
      currentTopic.minProficiencyThreshold
    ) {
      items.push({
        key: `topic-min-threshold:${snapshotTopic.id}`,
        label: `${snapshotTopic.name} min proficiency`,
        tabId: 'proficiency-goals',
        previousValue: snapshotTopic.minProficiencyThreshold,
        currentValue: currentTopic.minProficiencyThreshold,
        triggerActionType: 'target_proficiency_change',
      })
    }

    if (
      snapshotTopic.maxProficiencyThreshold !==
      currentTopic.maxProficiencyThreshold
    ) {
      items.push({
        key: `topic-max-threshold:${snapshotTopic.id}`,
        label: `${snapshotTopic.name} max proficiency`,
        tabId: 'proficiency-goals',
        previousValue: snapshotTopic.maxProficiencyThreshold,
        currentValue: currentTopic.maxProficiencyThreshold,
        triggerActionType: 'target_proficiency_change',
      })
    }

    if (snapshotTopic.includedInPlan !== currentTopic.includedInPlan) {
      items.push({
        key: `topic-included:${snapshotTopic.id}`,
        label: `${snapshotTopic.name} included in plan`,
        tabId: 'proficiency-goals',
        previousValue: snapshotTopic.includedInPlan ? 'included' : 'excluded',
        currentValue: currentTopic.includedInPlan ? 'included' : 'excluded',
        triggerActionType: 'target_proficiency_change',
      })
    }

    const currentSubtopicsById = new Map(
      currentTopic.subtopics.map((st) => [st.id, st]),
    )
    for (const snapshotSubtopic of snapshotTopic.subtopics) {
      const currentSubtopic = currentSubtopicsById.get(snapshotSubtopic.id)
      if (!currentSubtopic) continue

      const snapshotMin =
        snapshotSubtopic.minProficiencyThreshold ??
        snapshotTopic.minProficiencyThreshold
      const currentMin =
        currentSubtopic.minProficiencyThreshold ??
        currentTopic.minProficiencyThreshold

      if (snapshotMin !== currentMin) {
        items.push({
          key: `subtopic-min-threshold:${snapshotSubtopic.id}`,
          label: `${snapshotSubtopic.name} min proficiency`,
          tabId: 'proficiency-goals',
          previousValue: snapshotMin,
          currentValue: currentMin,
          triggerActionType: 'target_proficiency_change',
        })
      }

      const snapshotMax =
        snapshotSubtopic.maxProficiencyThreshold ??
        snapshotTopic.maxProficiencyThreshold
      const currentMax =
        currentSubtopic.maxProficiencyThreshold ??
        currentTopic.maxProficiencyThreshold

      if (snapshotMax !== currentMax) {
        items.push({
          key: `subtopic-max-threshold:${snapshotSubtopic.id}`,
          label: `${snapshotSubtopic.name} max proficiency`,
          tabId: 'proficiency-goals',
          previousValue: snapshotMax,
          currentValue: currentMax,
          triggerActionType: 'target_proficiency_change',
        })
      }

      const snapshotRollupWeight = snapshotSubtopic.rollupWeight ?? 1
      const currentRollupWeight = currentSubtopic.rollupWeight ?? 1

      if (snapshotRollupWeight !== currentRollupWeight) {
        items.push({
          key: `subtopic-rollup-weight:${snapshotSubtopic.id}`,
          label: `${snapshotSubtopic.name} rollup weight`,
          tabId: 'proficiency-goals',
          previousValue: snapshotRollupWeight,
          currentValue: currentRollupWeight,
          triggerActionType: 'target_proficiency_change',
        })
      }

      const snapshotEnabled = snapshotSubtopic.isEnabled !== false
      const currentEnabled = currentSubtopic.isEnabled !== false
      if (snapshotEnabled !== currentEnabled) {
        items.push({
          key: `subtopic-included:${snapshotSubtopic.id}`,
          label: `${snapshotSubtopic.name} included in plan`,
          tabId: 'proficiency-goals',
          previousValue: snapshotEnabled ? 'included' : 'excluded',
          currentValue: currentEnabled ? 'included' : 'excluded',
          triggerActionType: 'target_proficiency_change',
        })
      }
    }
  }
  return items
}

function comparePlan(
  snapshot: SnapshotState,
  current: CurrentState,
): DiffItem[] {
  const items: DiffItem[] = []
  if (
    snapshot.plan.weeklyCommitmentHours !== current.plan.weeklyCommitmentHours
  ) {
    items.push({
      key: 'plan:weekly-commitment',
      label: 'Weekly commitment (hours)',
      tabId: 'setup',
      previousValue: snapshot.plan.weeklyCommitmentHours,
      currentValue: current.plan.weeklyCommitmentHours,
      triggerActionType: 'weekly_commitment_change',
    })
  }
  if (snapshot.plan.targetDate !== current.plan.targetDate) {
    items.push({
      key: 'plan:target-date',
      label: 'Target date',
      tabId: 'setup',
      previousValue: snapshot.plan.targetDate ?? 'unset',
      currentValue: current.plan.targetDate ?? 'unset',
      triggerActionType: 'target_date_change',
    })
  }
  return items
}

export interface PendingDiffIngestionContext {
  knowledgeMapSeeded: boolean
  baselineEpochAt: Date | null
}

function compareCompletedWork(
  snapshot: SnapshotState,
  current: CurrentState,
  ingestion: PendingDiffIngestionContext,
): DiffItem[] {
  const items: DiffItem[] = []
  const currentById = new Map(
    current.completedWork.map((work) => [work.id, work]),
  )
  for (const snapshotWork of snapshot.completedWork) {
    const currentWork = currentById.get(snapshotWork.id)
    if (!currentWork) continue

    if (
      snapshotWork.reportedApproachType !==
      (currentWork.reportedApproach?.type ?? null)
    ) {
      items.push({
        key: `homework-approach:${snapshotWork.id}`,
        label: `${currentWork.problemTitle} reported approach`,
        tabId: 'homework-review',
        previousValue: snapshotWork.reportedApproachType ?? 'unset',
        currentValue: currentWork.reportedApproach?.type ?? 'unset',
        triggerActionType: 'homework_row_regrade',
      })
    }

    const ingestible = isAttemptIngestibleForEngine(
      ingestionInputFromCompletedWork(currentWork, ingestion),
    )
    if (
      snapshotWork.needsGrading &&
      !currentWork.needsGrading &&
      ingestible.eligible
    ) {
      items.push({
        key: `homework-graded:${snapshotWork.id}`,
        label: `${currentWork.problemTitle} grading status`,
        tabId: 'homework-review',
        previousValue: 'needs grading',
        currentValue: 'reviewed',
        triggerActionType: 'graded_homework',
      })
    }
  }
  return items
}

function compareRecommendations(
  snapshot: SnapshotState,
  current: CurrentState,
): DiffItem[] {
  const items: DiffItem[] = []
  const currentById = new Map(
    current.recommendations.map((recommendation) => [
      recommendation.id,
      recommendation,
    ]),
  )
  for (const snapshotRecommendation of snapshot.recommendations) {
    const currentRecommendation = currentById.get(snapshotRecommendation.id)
    if (!currentRecommendation) continue
    if (snapshotRecommendation.status !== currentRecommendation.status) {
      items.push({
        key: `recommendation-status:${snapshotRecommendation.id}`,
        label: `Recommendation ${snapshotRecommendation.id} status`,
        tabId: 'recommendations',
        previousValue: snapshotRecommendation.status,
        currentValue: currentRecommendation.status,
        triggerActionType: 'recommendation_status_change',
      })
    }
  }
  return items
}

function compareGraphJson(
  snapshot: SnapshotState,
  current: CurrentState,
): DiffItem[] {
  const previous = snapshot.graphJson
  const next = current.graphJson
  if (!previous && !next) return []
  const previousJson = JSON.stringify(previous ?? null)
  const nextJson = JSON.stringify(next ?? null)
  if (previousJson === nextJson) return []
  return [
    {
      key: 'graph:json-editor-state',
      label: 'Graph JSON editor state',
      tabId: 'graph',
      previousValue: previous ? 'saved' : 'unset',
      currentValue: next ? 'saved (modified)' : 'unset',
      triggerActionType: 'graph_json_change',
    },
  ]
}

export function derivePendingDiff(
  snapshot: SnapshotState | null,
  current: CurrentState,
  ingestion: PendingDiffIngestionContext = {
    knowledgeMapSeeded: false,
    baselineEpochAt: null,
  },
): PendingDiffResult {
  if (!snapshot) return { items: [], triggerActions: [] }

  const items = [
    ...compareTopicSettings(snapshot, current),
    ...comparePlan(snapshot, current),
    ...compareCompletedWork(snapshot, current, ingestion),
    ...compareRecommendations(snapshot, current),
    ...compareGraphJson(snapshot, current),
  ]
  return {
    items,
    triggerActions: items.map((item) => item.triggerActionType),
  }
}

export function deriveDirtyTabs(
  diff: PendingDiffResult,
): RecommendationEngineTabId[] {
  return [...new Set(diff.items.map((item) => item.tabId))]
}

export function formatDiffPreview(item: DiffItem): string {
  return `${item.previousValue} => ${item.currentValue}`
}

export function summarizePendingDiffProcessing(
  diff: PendingDiffResult,
): PendingProcessingSummary {
  return summarizePendingProcessing(diff.triggerActions)
}
